import axios, { AxiosRequestConfig } from 'axios'
import Cookies from 'js-cookie'
import { ResponseData } from './types'
import { refreshTokenApi } from './staking/refreshToken'

export const ACCESS_TOKEN = 'access_token'
export const REFRESH_TOKEN = 'refresh_token'
export const MAX_AGE_ACCESS_TOKEN = 1 // 1 days
export const MAX_AGE_REFRESH_TOKEN = 30 // 30 days

export const BASE_URL = `${process.env.NEXT_PUBLIC_API_BASE_URL}`
export const SECOND_BASE_URL = `${process.env.NEXT_PUBLIC_SECOND_API_BASE_URL}`

const createApiClient = (baseURL: string) => {
  const client = axios.create({
    timeout: 60000,
    baseURL,
  })

  // Request interceptor to attach access token from cookies
  client.interceptors.request.use(
    (config) => {
      const token = Cookies.get(ACCESS_TOKEN)
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
      return config
    },
    (error) => Promise.reject(error),
  )

  // Response interceptor to handle token expiration and refresh
  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config

      // If access token has expired (e.g., 401 unauthorized error)
      if (error.response.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true
        try {
          // Try refreshing the token (assuming there's an endpoint for that)
          const refreshToken = Cookies.get(REFRESH_TOKEN)
          if (refreshToken) {
            const response = await refreshTokenApi(refreshToken)
            if (response.success) {
              // Update cookies with new access token
              Cookies.set(ACCESS_TOKEN, response.data.access_token, {
                expires: MAX_AGE_ACCESS_TOKEN,
              })

              // Retry the original request with the new token
              client.defaults.headers.common['Authorization'] = `Bearer ${response.data.access_token}`
              return client(originalRequest)
            }
          }
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('Token refresh failed', err)
          // Optionally, redirect to login if refresh fails
        }
      }

      return Promise.reject(error)
    },
  )

  return client
}

// Create instances for both base URLs
const apiClient = createApiClient(BASE_URL)
const secondApiClient = createApiClient(SECOND_BASE_URL)

// Utility function to dynamically select a client
export const getApiClient = (useSecondBaseUrl = false) => (useSecondBaseUrl ? secondApiClient : apiClient)

export const doInternalRequest = async (
  action: string,
  method: 'get' | 'post' | 'options' | 'put' | 'patch',
  data?: object,
  config?: AxiosRequestConfig,
  useSecondBaseUrl = false,
) => {
  const client = getApiClient(useSecondBaseUrl)
  const requestMethod = (method || 'get').toUpperCase()
  try {
    const result = await client.request({
      url: action,
      method: requestMethod,
      data: data,
      ...config,
    })
    return {
      success: true,
      message: result.statusText,
      data: result.data,
    } as ResponseData
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    return {
      success: false,
      message: error.statusText,
      data: error.data,
    } as ResponseData
  }
}

export const getFromApi = async (action: string, config?: AxiosRequestConfig, useSecondBaseUrl = false) => {
  return await doInternalRequest(action, 'get', undefined, config, useSecondBaseUrl)
}

export const postToApi = async (
  action: string,
  data?: object,
  config?: AxiosRequestConfig,
  useSecondBaseUrl = false,
) => {
  return await doInternalRequest(action, 'post', data, config, useSecondBaseUrl)
}

export const patchToApi = async (
  action: string,
  data?: object,
  config?: AxiosRequestConfig,
  useSecondBaseUrl = false,
) => {
  return await doInternalRequest(action, 'patch', data, config, useSecondBaseUrl)
}

export default apiClient
